import {
    AfterViewInit,
    Component,
    computed,
    inject,
    OnDestroy,
    OnInit,
    signal,
    TemplateRef,
    viewChild,
} from '@angular/core';
import { Button, Dropdown, DropdownOption } from '@ntv360/component-pantry';
import {
    DropdownComponent,
    StatsCardComponent,
    TableComponent,
    TableToolbarButton,
    TableToolbarComponent,
} from '@shared';

import { ExportService, TimezoneService, UsersService } from '@core';
import { SubNavbarService } from '@layouts/main-layout/components/sub-navbar/sub-navbar.service';
import { SVG_ICONS } from '@shared/constants';
import {
    ADVERTISERS_COLUMNS,
    ADVERTISERS_DATA,
    ADVERTISERS_STATS,
} from './constants/advertisers-table.constants';
import { Advertisers } from './interfaces';

import { ConfirmationModalComponent } from '@shared/components/modals/confirmation-modal/confirmation-modal.component';
import { Logger, mapTimezonesForDropdown, withLoading } from '@shared/utils';
import { catchError, EMPTY } from 'rxjs';

@Component({
    selector: 'app-advertisers',
    standalone: true,
    imports: [
        StatsCardComponent,
        TableToolbarComponent,
        Button,
        TableComponent,
        ConfirmationModalComponent,
        DropdownComponent,
    ],
    templateUrl: './advertisers.component.html',
    styleUrl: './advertisers.component.scss',
})
export class AdvertisersComponent implements OnInit, AfterViewInit, OnDestroy {
    /**
     * Injected export service for CSV export.
     */
    private exportService = inject(ExportService);

    /**
     * Sub-Navbar service used to programmatically project content into its view
     */
    protected subNavbarService = inject(SubNavbarService);

    /**
     * Services in managing and caching timezone responses
     */
    private timezoneService = inject(TimezoneService);

    /**
     * Manages users data operations and API interactions
     */
    private usersService = inject(UsersService);

    /**
     * Content reference to project into the sub-navbar component
     */
    protected subNavBarContent = viewChild<TemplateRef<any>>('subNavBarContent');

    /**
     * Reference to timezone dropdown component
     */
    protected timezoneDropdown = viewChild<Dropdown>('timezoneDropdown');

    /**
     * Reference to navbar icon constants.
     */
    public icons = SVG_ICONS;

    /**
     * Path to the SVG sprite file.
     */
    public spritePath = 'assets/icons/icons-sprite.svg';

    /**
     * List of stats cards to display.
     */
    public statsData = computed(() => ADVERTISERS_STATS);

    /**
     * Table columns definition.
     */
    public advertisersColumns = ADVERTISERS_COLUMNS;

    /**
     * Table row data.
     */
    public advertisersData = ADVERTISERS_DATA;

    /**
     * Signal to track selected rows in the table.
     */
    public selectedRows = signal<Advertisers[]>([]);

    /**
     * Whether to show delete confirmation modal
     */
    public showDeleteConfirmationModal = signal<boolean>(false);

    /**
     * Holds the selected advertiser data for actions like delete
     */
    public selectedAdvertiser = signal<Advertisers | null>(null);

    /**
     * Configuration object for the timezone dropdown, including options and placeholder.
     */
    public timezoneConfig = signal({
        options: [] as { label: string; value: number }[],
        placeholder: 'Timezone',
        fullWidth: true,
    });

    /**
     * Flag indicating when timezone data is being fetched
     */
    public isLoadingTimezones = signal<boolean>(false);

    /**
     * Computed list of toolbar buttons, reacting to selection changes.
     */
    public toolbarButtons = computed<TableToolbarButton[]>(() => {
        const selectionCount = this.selectedRows().length;
        return [
            {
                buttonName: 'Bulk Delete',
                icon: 'delete',
                variant: selectionCount > 1 ? 'danger' : 'secondary',
            },
            {
                buttonName: 'Export',
                icon: 'export',
                variant: 'accent',
            },
        ];
    });

    /**
     * Load all available timezones on init
     */
    ngOnInit(): void {
        this.loadTimezones();
    }

    /**
     * Project the content into the sub-navbar component after view initializes
     */
    ngAfterViewInit(): void {
        if (this.subNavBarContent()) {
            this.subNavbarService.setContent(this.subNavBarContent()!);
        }
    }

    /**
     * Clear the projected content from the sub-navbar component
     */
    ngOnDestroy(): void {
        this.subNavbarService.clearContent();
    }

    /**
     * Handles toolbar action click events.
     *
     * @param action - The action name
     */
    public onToolbarAction(action: string): void {
        switch (action) {
            case 'Bulk Delete':
                this.showDeleteConfirmationModal.set(true);
                break;
            case 'Export':
                const dataToExport =
                    this.selectedRows().length > 0 ? this.selectedRows() : this.advertisersData;

                this.exportService.exportToExcel(
                    dataToExport,
                    'Advertisers',
                    this.advertisersColumns,
                );
        }
    }

    /**
     * Handles action click events from the table.
     *
     * @param event - The action event containing action name and row data
     */
    public onActionClick(event: { action: string; row: Advertisers }): void {
        const { action, row } = event;

        if (action.toLowerCase() === 'delete') {
            this.selectedAdvertiser.set(row);
            this.showDeleteConfirmationModal.set(true);
        }
    }

    /**
     * Handles closing delete confirmation modal.
     */
    public closeModal(): void {
        this.showDeleteConfirmationModal.set(false);
        this.selectedAdvertiser.set(null);
    }

    /**
     * Handles confirming deleting advertiser.
     */
    public onConfirmDelete(): void {
        const selectedAdvertiser = this.selectedAdvertiser();
        if (!selectedAdvertiser) return;

        this.closeModal();
    }

    /**
     * Handles cell click events from the table.
     *
     * @param event - The cell event containing field name, value, and row data
     */
    public onCellClick(event: { field: string; value: any; row: any }): void {
        if (event.row.id) {
            const url = `/advertisers/${event.row.id}`;
            window.open(url, '_blank');
        }
    }

    /**
     * Handles selection change events from the table.
     *
     * @param selectedRows - The array of currently selected rows
     */
    public onSelectionChange(selectedRows: Advertisers[]): void {
        this.selectedRows.set(selectedRows);
    }

    /**
     * Handles timezone selection change events from the dropdown.
     *
     * @param event - The timezone selection event containing the selected timezone value
     */
    public onTimezoneChange(event: DropdownOption | null): void {
        if (!event) return;
    }

    /**
     * Handles button click from sub-navbar
     */
    public onCreateAdvertiser(): void {
        window.open('/advertisers/create', '_blank');
    }

    /** =================================== API Calls =========================================== */

    /** Loads all available timezones */
    private loadTimezones(): void {
        this.timezoneService
            .getAll()
            .pipe(
                withLoading(this.isLoadingTimezones),
                catchError((err) => {
                    Logger.error('AdvertisersComponent', 'Failed to load timezones', err);
                    return EMPTY;
                }),
            )
            .subscribe((res) => {
                this.timezoneConfig.update((config) => {
                    return {
                        ...config,
                        options: mapTimezonesForDropdown(res.data),
                    };
                });
            });
    }
}
